import { useState } from "react";
import { motion } from "framer-motion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, Subsystem } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DailyTrendChart from "@/components/DailyTrendChart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, PowerOff } from "lucide-react";
import { cn, relativeTime } from "@/lib/utils";

const stateMeta: Record<
  Subsystem["state"],
  { icon: React.ComponentType<{ className?: string }>; color: string; label: string }
> = {
  ok: { icon: CheckCircle2, color: "text-success", label: "Działa" },
  warn: { icon: AlertTriangle, color: "text-warning", label: "Ostrzeżenie" },
  down: { icon: XCircle, color: "text-destructive", label: "Awaria" },
  off: { icon: PowerOff, color: "text-muted-foreground", label: "Wyłączone" },
};

function formatUsd(v: number | null | undefined, digits = 2): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `$${Number(v).toFixed(digits)}`;
}

function formatDepletes(iso: string | null | undefined): string {
  if (!iso || iso.length < 10) return "za mało danych";
  return `ok. ${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

export default function Status() {
  const qc = useQueryClient();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["status"],
    queryFn: api.listStatus,
    refetchInterval: 30_000,
  });
  const billingQ = useQuery({
    queryKey: ["status", "gemini-billing"],
    queryFn: () => api.geminiBilling(30),
    refetchInterval: 60_000,
  });
  const [balanceInput, setBalanceInput] = useState("");
  const setBalanceM = useMutation({
    mutationFn: (balance_usd: number) => api.setGeminiBalance({ balance_usd }),
    onSuccess: (snap) => {
      qc.setQueryData(["status", "gemini-billing"], snap);
      qc.invalidateQueries({ queryKey: ["status"] });
      setBalanceInput("");
    },
  });

  const rows = data ?? [];
  const bill = billingQ.data;

  return (
    <div>
      <PageHeader
        title="Status"
        subtitle="Stan aplikacji i podsystemów"
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void refetch();
              void billingQ.refetch();
            }}
            disabled={isFetching || billingQ.isFetching}
          >
            <RefreshCw
              className={cn(
                "mr-2 h-3.5 w-3.5",
                (isFetching || billingQ.isFetching) && "animate-spin",
              )}
            />
            Odśwież
          </Button>
        }
      />

      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Gemini prepaid</CardTitle>
          <p className="text-xs text-muted-foreground">
            {bill?.sources_note || "szacunek: karuzele + Bart + narada"}. Google nie daje API salda;
            po doładowaniu w AI Studio wpisz tu kwotę.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Saldo wpisane</p>
              <p className="text-lg font-semibold tabular-nums">{formatUsd(bill?.balance_usd)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Spalone od wpisu</p>
              <p className="text-lg font-semibold tabular-nums">
                {formatUsd(bill?.spent_since_set, 4)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Pozostało</p>
              <p className="text-lg font-semibold tabular-nums text-success">
                {formatUsd(bill?.remaining_usd)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Śr. spalanie / dzień
              </p>
              <p className="text-lg font-semibold tabular-nums">
                {formatUsd(bill?.avg_daily_usd ?? 0, 4)}
              </p>
              <p className="text-xs text-muted-foreground">
                Wyczerpie się: {formatDepletes(bill?.depletes_on)}
                {bill?.days_left != null ? ` (~${bill.days_left} d)` : ""}
              </p>
            </div>
          </div>

          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const n = Number(String(balanceInput).replace(",", "."));
              if (!Number.isFinite(n) || n < 0) return;
              setBalanceM.mutate(n);
            }}
          >
            <div className="min-w-[140px] flex-1">
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor="gemini-balance">
                Ustaw saldo ($)
              </label>
              <Input
                id="gemini-balance"
                type="number"
                min={0}
                step="0.01"
                placeholder="np. 50"
                value={balanceInput}
                onChange={(e) => setBalanceInput(e.target.value)}
              />
            </div>
            <Button type="submit" size="sm" disabled={setBalanceM.isPending || !balanceInput.trim()}>
              {setBalanceM.isPending ? "Zapis…" : "Ustaw saldo"}
            </Button>
            {bill?.set_at_utc ? (
              <p className="text-xs text-muted-foreground">
                Ostatni wpis: {relativeTime(bill.set_at_utc)}
              </p>
            ) : null}
          </form>
          {setBalanceM.isError ? (
            <p className="text-sm text-destructive">Nie udało się zapisać salda.</p>
          ) : null}
        </CardContent>
      </Card>

      <DailyTrendChart
        className="mb-4"
        title="Spalanie Gemini ($ / dzień)"
        subtitle="Ostatnie 30 dni · lokalny meter"
        data={(bill?.daily || []).map((d) => ({ date: d.date, usd: d.usd }))}
        series={[{ key: "usd", label: "USD", format: "usd", color: "hsl(var(--brand))" }]}
        mode="bar"
        hideForSingleDay={false}
        emptyNote="Brak zarejestrowanego spalania. Pojawi się po wywołaniach karuzel / Barta / narady."
      />

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 text-left font-medium">Komponent</th>
                <th className="px-5 py-3 text-left font-medium">Stan</th>
                <th className="px-5 py-3 text-left font-medium">Ostatni sygnał</th>
                <th className="px-5 py-3 text-left font-medium">Uwagi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-sm text-muted-foreground">
                    Ładowanie…
                  </td>
                </tr>
              )}
              {rows.map((r, i) => {
                const meta = stateMeta[r.state];
                const Icon = meta.icon;
                return (
                  <motion.tr
                    key={r.name}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="border-b border-border/30 last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-5 py-3 text-sm font-medium">{r.name}</td>
                    <td className="px-5 py-3">
                      <Badge
                        variant={
                          r.state === "ok"
                            ? "success"
                            : r.state === "warn"
                            ? "warning"
                            : r.state === "down"
                            ? "destructive"
                            : "secondary"
                        }
                        className="gap-1.5"
                      >
                        <Icon className={cn("h-3 w-3", meta.color)} />
                        {meta.label}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">
                      {r.last_seen ? relativeTime(r.last_seen) : "-"}
                    </td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">
                      {r.last_error || r.note || "-"}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
